import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { captureLocation } from '../../lib/workerData';
import { formatTime, formatDate } from '../../lib/utils';
import { s } from '../../lib/workerStyles';

export default function InspectionScreen({ building, profile, onBack }) {
  const [score, setScore] = useState(0);
  const [areasChecked, setAreasChecked] = useState([]);
  const [deficiencies, setDeficiencies] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [inspNotes, setInspNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [gps, setGps] = useState(null);

  const areas = [
    'Washrooms', 'Floors', 'General Areas', 'Kitchen/Eating Areas',
    'Change Rooms', 'Windows/Glass', 'Garbage/Recycling', 'Supplies/Dispensers',
    'Offices', 'Concourse/Hallways', 'Outside Areas'
  ];

  useEffect(() => {
    captureLocation(building).then(setGps);
  }, [building]);

  function toggleArea(area) {
    setAreasChecked(prev => prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]);
  }

  async function handleSubmit() {
    if (score === 0 || areasChecked.length === 0) return;
    setSubmitting(true);
    try {
      await supabase.from('supervisor_inspections').insert([{
        building_id: building.id,
        inspector_id: profile?.id,
        inspector_name: profile?.full_name || 'Supervisor',
        overall_score: score,
        areas_checked: areasChecked,
        deficiencies: deficiencies || null,
        corrective_action: correctiveAction || null,
        notes: inspNotes || null,
        gps_lat: gps?.lat || null,
        gps_lng: gps?.lng || null,
        gps_verified: gps?.verified || false,
      }]);
      setSubmitted(true);
    } catch (err) {
      alert('Error saving inspection: ' + err.message);
    }
    setSubmitting(false);
  }

  if (submitted) {
    return (
      <div style={s.screen}>
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', marginBottom: 8 }}>Inspection Submitted</div>
          <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 8 }}>{building.id} — {building.name}</div>
          <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 4 }}>Score: {'⭐'.repeat(score)} ({score}/5)</div>
          <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 4 }}>{areasChecked.length} areas inspected</div>
          {deficiencies && <div style={{ fontSize: 13, color: '#f59e0b', marginBottom: 4 }}>Deficiencies noted</div>}
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 24 }}>{formatDate(new Date())} · {formatTime(new Date())}</div>
          <button style={{ ...s.buildingCard, justifyContent: 'center', padding: '14px 0' }} onClick={onBack}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#f97316' }}>← Back to Buildings</span>
          </button>
        </div>
      </div>
    );
  }

  const scoreLabels = ['', 'Poor', 'Below Standard', 'Acceptable', 'Good', 'Excellent'];
  const scoreColors = ['', '#ef4444', '#f59e0b', '#eab308', '#22c55e', '#10b981'];

  return (
    <div style={s.screen}>
      <div style={s.navBar}>
        <button style={s.backBtn} onClick={onBack}>‹ Back</button>
        <div style={s.navTitle}>Inspection</div>
        <div style={{ width: 40 }} />
      </div>

      <div style={{ padding: '0 16px' }}>
        {/* Building info */}
        <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>SUPERVISOR INSPECTION</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#f1f5f9', marginTop: 4 }}>{building.id} — {building.name}</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>{building.address}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Inspector: {profile?.full_name} · {formatDate(new Date())}</div>
          {gps?.verified && <div style={{ fontSize: 11, color: '#22c55e', marginTop: 4 }}>📍 On-site verified</div>}
          {gps && !gps.verified && <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>📍 {gps.distance_metres || '?'}m from building</div>}
        </div>

        {/* Overall Score */}
        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>OVERALL QUALITY SCORE *</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          {[1,2,3,4,5].map(n => (
            <button key={n} onClick={() => setScore(n)}
              style={{
                flex: 1, padding: '12px 0', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                border: score === n ? `2px solid ${scoreColors[n]}` : '2px solid #334155',
                background: score === n ? (n <= 2 ? '#450a0a' : n === 3 ? '#422006' : '#052e16') : '#0f172a',
                color: score === n ? scoreColors[n] : '#64748b', fontSize: 18, fontWeight: 800,
              }}>
              {n}
            </button>
          ))}
        </div>
        {score > 0 && <div style={{ fontSize: 13, fontWeight: 700, color: scoreColors[score], marginBottom: 16 }}>{scoreLabels[score]}</div>}
        {score === 0 && <div style={{ fontSize: 12, color: '#475569', marginBottom: 16 }}>1 = Poor, 5 = Excellent</div>}

        {/* Areas Checked */}
        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>AREAS INSPECTED *</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {areas.map(area => {
            const checked = areasChecked.includes(area);
            return (
              <button key={area} onClick={() => toggleArea(area)}
                style={{
                  padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  border: checked ? '1px solid #a78bfa' : '1px solid #334155',
                  background: checked ? '#2e1065' : '#0f172a',
                  color: checked ? '#c4b5fd' : '#94a3b8',
                }}>
                {checked ? '✓ ' : ''}{area}
              </button>
            );
          })}
        </div>

        {/* Deficiencies */}
        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>DEFICIENCIES FOUND</div>
        <textarea
          style={{ ...s.input, minHeight: 60, resize: 'vertical', fontFamily: 'inherit', marginBottom: 12 }}
          placeholder="Describe any issues found (leave blank if none)..."
          value={deficiencies}
          onChange={e => setDeficiencies(e.target.value)}
        />

        {/* Corrective Action */}
        {deficiencies && (
          <>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>CORRECTIVE ACTION TAKEN</div>
            <textarea
              style={{ ...s.input, minHeight: 60, resize: 'vertical', fontFamily: 'inherit', marginBottom: 12 }}
              placeholder="What was done to address the issues..."
              value={correctiveAction}
              onChange={e => setCorrectiveAction(e.target.value)}
            />
          </>
        )}

        {/* Notes */}
        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>ADDITIONAL NOTES</div>
        <textarea
          style={{ ...s.input, minHeight: 50, resize: 'vertical', fontFamily: 'inherit', marginBottom: 16 }}
          placeholder="Any other observations..."
          value={inspNotes}
          onChange={e => setInspNotes(e.target.value)}
        />

        {/* Submit */}
        <button
          style={{
            width: '100%', padding: '16px 0', borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: 'pointer',
            background: (score > 0 && areasChecked.length > 0 && !submitting) ? '#a78bfa' : '#334155',
            color: (score > 0 && areasChecked.length > 0) ? '#000' : '#64748b',
            border: 'none', marginBottom: 20,
          }}
          disabled={score === 0 || areasChecked.length === 0 || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Submitting...' : 'Submit Inspection'}
        </button>
      </div>
    </div>
  );
}